import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = ["Identificación", "Tecnología", "Datos", "Uso e impacto", "Evaluación inicial"];

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-muted-foreground">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
    {children}
  </div>
);

export default function NewInitiative() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || {};
  const [catalogs, setCatalogs] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", description: "", use_case: "", department: "", business_owner: "", tech_owner: "", clinical_owner: "",
    provider: "", model: "", model_version: "1.0", asset_type: "Caso de uso", integrations: "",
    data_types: "", data_classification: "Interna", personal_data: false, clinical_data: false, sensitive_data: false,
    retention: "12 meses", processing_region: "Chile",
    estimated_users: 10, usage_volume: "", automated_decisions: false, human_validation: true, expected_value: "Medio", roi_estimate: "",
    inherent_risk: "Medio",
    ...prefill,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/catalogs").then(({ data }) => setCatalogs(data)); }, []);

  const validators = [
    () => f.name && f.department && f.business_owner ? null : "Complete nombre, departamento y responsable",
    () => f.provider ? null : "Seleccione un proveedor",
    () => f.data_classification ? null : "Seleccione clasificación de datos",
    () => null,
    () => null,
  ];

  const next = () => {
    const err = validators[step]();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const suggestedControls = [
    f.clinical_data && "Validación clínica",
    f.personal_data && "Revisión de Protección de Datos",
    f.automated_decisions && "Human-in-the-loop",
    "Registro de interacciones",
    f.provider && f.provider !== "Interno UC CHRISTUS" && "Revisión contractual",
  ].filter(Boolean);

  const route = f.clinical_data || f.inherent_risk === "Alto" || f.inherent_risk === "Crítico" || f.inherent_risk === "Medio-Alto"
    ? "Comité de IA (con revisiones DPO / clínica según corresponda)"
    : "Aprobación del área responsable";

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        integrations: f.integrations ? f.integrations.split(",").map((s) => s.trim()).filter(Boolean) : [],
        data_types: f.data_types ? f.data_types.split(",").map((s) => s.trim()).filter(Boolean) : [],
        estimated_users: Number(f.estimated_users) || 0,
      };
      const { data } = await api.post("/initiatives", { data: payload, reason: "Registro mediante flujo guiado" });
      toast.success(`Iniciativa ${data.code} creada. Se generó la tarea de evaluación de riesgo.`);
      navigate(`/iniciativas/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!catalogs) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Registrar nueva iniciativa" subtitle="Flujo guiado de registro. Al finalizar se crea la iniciativa, se registra el evento de auditoría y se generan las tareas de evaluación." testId="wizard-header" />

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => i < step && setStep(i)} className={cn("text-[11px] sm:text-xs font-medium transition-colors", i === step ? "text-primary" : i < step ? "text-accent" : "text-muted-foreground")}>
              {i < step && <CheckCircle2 className="h-3 w-3 inline mr-1" />}{i + 1}. {s}
            </button>
          ))}
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
      </div>

      <Card className="shadow-card border-border">
        <CardContent className="p-6 sm:p-8 space-y-5" data-testid={`wizard-step-${step}`}>
          {step === 0 && (<>
            <Field label="Nombre de la iniciativa" required><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej: Asistente de triage digital" data-testid="wizard-name" /></Field>
            <Field label="Descripción"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Qué hace la solución y en qué contexto se utiliza" data-testid="wizard-description" /></Field>
            <Field label="Problema que resuelve / caso de uso"><Input value={f.use_case} onChange={(e) => set("use_case", e.target.value)} placeholder="Ej: reducción de tiempos de espera" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Departamento" required>
                <Select value={f.department} onValueChange={(v) => set("department", v)}>
                  <SelectTrigger data-testid="wizard-department"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{catalogs.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Business owner" required><Input value={f.business_owner} onChange={(e) => set("business_owner", e.target.value)} placeholder="Nombre del responsable" data-testid="wizard-owner" /></Field>
            </div>
          </>)}

          {step === 1 && (<>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Proveedor" required>
                <Select value={f.provider} onValueChange={(v) => set("provider", v)}>
                  <SelectTrigger data-testid="wizard-provider"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{catalogs.providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Tipo de activo">
                <Select value={f.asset_type} onValueChange={(v) => set("asset_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{catalogs.asset_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Modelo o servicio"><Input value={f.model} onChange={(e) => set("model", e.target.value)} placeholder="Ej: GPT-4o, modelo interno" data-testid="wizard-model" /></Field>
              <Field label="Versión"><Input value={f.model_version} onChange={(e) => set("model_version", e.target.value)} /></Field>
            </div>
            <Field label="Integraciones y dependencias (separadas por coma)"><Input value={f.integrations} onChange={(e) => set("integrations", e.target.value)} placeholder="HIS, ERP SAP, Teams" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Responsable técnico"><Input value={f.tech_owner} onChange={(e) => set("tech_owner", e.target.value)} /></Field>
              <Field label="Responsable clínico (si corresponde)"><Input value={f.clinical_owner} onChange={(e) => set("clinical_owner", e.target.value)} /></Field>
            </div>
          </>)}

          {step === 2 && (<>
            <Field label="Tipos de datos procesados (separados por coma)"><Input value={f.data_types} onChange={(e) => set("data_types", e.target.value)} placeholder="Ficha clínica, datos demográficos" data-testid="wizard-datatypes" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Clasificación de datos" required>
                <Select value={f.data_classification} onValueChange={(v) => set("data_classification", v)}>
                  <SelectTrigger data-testid="wizard-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>{catalogs.data_classifications.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Ubicación de procesamiento"><Input value={f.processing_region} onChange={(e) => set("processing_region", e.target.value)} /></Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[["personal_data", "Datos personales"], ["clinical_data", "Datos clínicos"], ["sensitive_data", "Datos sensibles"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span className="text-sm">{l}</span>
                  <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`wizard-${k}`} />
                </div>
              ))}
            </div>
            <Field label="Retención de datos"><Input value={f.retention} onChange={(e) => set("retention", e.target.value)} /></Field>
          </>)}

          {step === 3 && (<>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Usuarios estimados"><Input type="number" value={f.estimated_users} onChange={(e) => set("estimated_users", e.target.value)} data-testid="wizard-users" /></Field>
              <Field label="Volumen de uso estimado"><Input value={f.usage_volume} onChange={(e) => set("usage_volume", e.target.value)} placeholder="~2.000 consultas/mes" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><p className="text-sm">Decisiones automatizadas</p><p className="text-[11px] text-muted-foreground">Sin intervención humana previa</p></div>
                <Switch checked={f.automated_decisions} onCheckedChange={(v) => set("automated_decisions", v)} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><p className="text-sm">Validación humana</p><p className="text-[11px] text-muted-foreground">Human-in-the-loop</p></div>
                <Switch checked={f.human_validation} onCheckedChange={(v) => set("human_validation", v)} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Valor esperado">
                <Select value={f.expected_value} onValueChange={(v) => set("expected_value", v)}>
                  <SelectTrigger data-testid="wizard-value"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Muy alto", "Alto", "Medio", "Bajo"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="ROI o impacto estimado"><Input value={f.roi_estimate} onChange={(e) => set("roi_estimate", e.target.value)} placeholder="Ej: -20% tiempo de espera" /></Field>
            </div>
          </>)}

          {step === 4 && (<>
            <Field label="Riesgo preliminar">
              <Select value={f.inherent_risk} onValueChange={(v) => set("inherent_risk", v)}>
                <SelectTrigger data-testid="wizard-risk"><SelectValue /></SelectTrigger>
                <SelectContent>{catalogs.risk_levels.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="rounded-xl border border-border bg-gradient-panel p-5 space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Controles sugeridos</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {suggestedControls.map((c) => <span key={c} className="px-2.5 py-1 rounded-md bg-accent/10 text-accent border border-accent/25 text-xs font-medium">{c}</span>)}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ruta de aprobación</p>
                <p className="text-sm mt-1.5">{route}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Evidencias necesarias</p>
                <p className="text-sm mt-1.5 text-muted-foreground">{f.clinical_data ? "DPIA, validación clínica, documentación del proveedor, plan de monitoreo" : f.personal_data ? "DPIA, documentación del proveedor, plan de monitoreo" : "Documentación del proveedor y caso de negocio"}</p>
              </div>
            </div>
          </>)}
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => step === 0 ? navigate(-1) : setStep(step - 1)} className="border-border" data-testid="wizard-back-btn">
          <ChevronLeft className="h-4 w-4 mr-1" /> {step === 0 ? "Cancelar" : "Anterior"}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="wizard-next-btn">
            Siguiente <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="wizard-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear iniciativa"}
          </Button>
        )}
      </div>
    </div>
  );
}
